import equal from "fast-deep-equal";
import React, { useEffect } from "react";
import { useSelector } from "react-redux";

import useMediaQuery from "@services/breakpoint.hooks";

import { useThunkDispatch } from "@redux/dispatcher";
import {
    addProductDirectly,
    createWishlist,
    loadAll,
} from "@redux/modules/api/data/wishlist/wishlist.module";
import {
    getLastUsedWishlist,
    getWishlists,
    isLoading,
} from "@redux/modules/api/data/wishlist/wishlist.selectors";
import { useGranite } from "@redux/modules/value/translations/translations.provider";
import { set as setWishlistModal } from "@redux/modules/value/wishlistModal/wishlistModal.module";
import {
    getWishlistModalProduct,
    getWishlistModalVisible,
} from "@redux/modules/value/wishlistModal/wishlistModal.selectors";

import { HuboModal } from "@components/General/HuboModal/huboModal.component";
import {
    WishListActionable,
    WishlistActionableMode,
} from "@components/Wishlist/WishlistActionable/WishListActionable.component";

const FORM_ID = "add-to-wishlist";

const AddToWishlistModal = () => {
    const { t } = useGranite();
    const isSmallScreen = useMediaQuery("screen and (max-height: 530px)");
    const title = t("wishlist.addToWishlist");
    const dispatch = useThunkDispatch();
    const wishlists = useSelector(getWishlists, equal);
    const wishlistsLoading = useSelector(isLoading);
    const visible = useSelector(getWishlistModalVisible);
    const modalProduct = useSelector(getWishlistModalProduct);
    const lastUpdatedWishlist = useSelector(getLastUsedWishlist);

    useEffect(() => {
        dispatch(loadAll());
    }, []);

    const submit = async ({
        wishlistId,
        wishlistName,
    }: {
        wishlistId?: string;
        wishlistName?: string;
    }) => {
        if (!modalProduct) return;

        if (wishlistId) {
            await dispatch(
                addProductDirectly(wishlistId, modalProduct.sku, {
                    product: {
                        ean: modalProduct.ean,
                        productTitle: modalProduct.productTitle,
                        sku: modalProduct.sku,
                        analytics: modalProduct.analytics,
                        brandName: modalProduct.brandName,
                        currentPrice: modalProduct.currentPrice,
                        roundedDiscountPercentage:
                            modalProduct.roundedDiscountPercentage,
                    },
                }),
            );
            return dispatch(setWishlistModal({ visible: false }));
        }

        if (wishlistName) {
            await dispatch(
                createWishlist(wishlistName, [{ sku: modalProduct.sku }]),
            );
            return dispatch(setWishlistModal({ visible: false }));
        }
    };

    return (
        <HuboModal
            modalState={!!visible}
            autoFocus={false}
            toggleHandler={state =>
                dispatch(setWishlistModal({ visible: state }))
            }
            title={title}
            backdrop={true}
            size="sm"
            formId={FORM_ID}
            withFooter={true}
            scrollable={!isSmallScreen}
            centered={!isSmallScreen}
            confirmButtonText={t("general.address.modal.button.label")}
        >
            <div className="my-3">
                <WishListActionable
                    formId={FORM_ID}
                    wishlists={wishlists}
                    loading={wishlistsLoading}
                    mode={WishlistActionableMode.ADD}
                    currentWishlistId={lastUpdatedWishlist?.id}
                    submit={submit}
                />
            </div>
        </HuboModal>
    );
};

export { AddToWishlistModal };
